import React, { useState, useEffect } from 'react';
import {
  Plus,
  Edit,
  Trash2,
  Car,
  Eye,
  Loader2,
  AlertCircle,
  CheckCircle
} from 'lucide-react';
import { vehicleAPI } from '../services/vehicleAPI';
import { Vehicle } from '../types';
import VehicleFormModal from '../components/vehicles/VehicleFormModal';
import VehicleDetailModal from '../components/vehicles/VehicleDetailModal';

const VehiclesPage: React.FC = () => {
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState<Vehicle | null>(null);

  // Delete confirmation
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [vehicleToDelete, setVehicleToDelete] = useState<Vehicle | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchVehicles();
  }, []);

  const fetchVehicles = async () => {
    try {
      setLoading(true);
      const response = await vehicleAPI.getMyVehicles();
      setVehicles(response.vehicles);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch vehicles');
    } finally {
      setLoading(false);
    }
  };

  const handleAddVehicle = () => {
    setSelectedVehicle(null);
    setShowAddModal(true);
  };

  const handleEditVehicle = (vehicle: Vehicle) => {
    setSelectedVehicle(vehicle);
    setShowEditModal(true);
  };

  const handleViewVehicle = (vehicle: Vehicle) => {
    setSelectedVehicle(vehicle);
    setShowDetailModal(true);
  };

  const handleDeleteVehicle = (vehicle: Vehicle) => {
    setVehicleToDelete(vehicle);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteVehicle = async () => {
    if (!vehicleToDelete) return;

    try {
      setDeleting(true);
      await vehicleAPI.deleteVehicle(vehicleToDelete.id);
      setSuccess('Vehicle deleted successfully');
      await fetchVehicles();
      setShowDeleteConfirm(false);
      setVehicleToDelete(null);

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete vehicle');
    } finally {
      setDeleting(false);
    }
  };

  const handleVehicleUpdated = async () => {
    await fetchVehicles();
    setShowAddModal(false);
    setShowEditModal(false);
    setSelectedVehicle(null);
    setSuccess('Vehicle saved successfully');

    // Clear success message after 3 seconds
    setTimeout(() => setSuccess(null), 3000);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex items-center space-x-2">
          <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
          <span className="text-gray-600">Loading vehicles...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="flex items-center justify-center w-12 h-12 bg-blue-600 text-white rounded-lg">
                <Car className="h-6 w-6" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">My Vehicles</h1>
                <p className="text-gray-600">Manage your registered vehicles</p>
              </div>
            </div>
            <button
              onClick={handleAddVehicle}
              className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus className="h-5 w-5" />
              <span>Add Vehicle</span>
            </button>
          </div>
        </div>

        {/* Status Messages */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center space-x-2">
            <AlertCircle className="h-5 w-5 text-red-600" />
            <span className="text-red-800">{error}</span>
            <button
              onClick={() => setError(null)}
              className="ml-auto text-red-600 hover:text-red-800"
            >
              ×
            </button>
          </div>
        )}

        {success && (
          <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4 flex items-center space-x-2">
            <CheckCircle className="h-5 w-5 text-green-600" />
            <span className="text-green-800">{success}</span>
            <button
              onClick={() => setSuccess(null)}
              className="ml-auto text-green-600 hover:text-green-800"
            >
              ×
            </button>
          </div>
        )}

        {/* Vehicles Grid */}
        {vehicles.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
            <Car className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No vehicles yet</h3>
            <p className="text-gray-600 mb-6">Add your first vehicle to get started.</p>
            <button
              onClick={handleAddVehicle}
              className="inline-flex items-center space-x-2 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus className="h-5 w-5" />
              <span>Add Your First Vehicle</span>
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {vehicles.map((vehicle) => (
              <div
                key={vehicle.id}
                className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow"
              >
                {/* Vehicle Image */}
                <div className="relative h-48 bg-gray-200">
                  {vehicle.picture_url ? (
                    <img
                      src={BACKEND_URL + vehicle.picture_url}
                      alt={`${vehicle.make} ${vehicle.model}`}
                      className="w-full h-full object-cover"
                      crossOrigin="anonymous"
                      onError={(e) => {
                        const img = e.target as HTMLImageElement;
                        console.warn('Image load failed for vehicle:', vehicle.id, 'URL:', BACKEND_URL + vehicle.picture_url);
                        // Try the API endpoint as fallback
                        if (!img.src.includes('/api/vehicles/')) {
                          img.src = `${BACKEND_URL}/api/vehicles/${vehicle.id}/picture`;
                        } else {
                          // If API endpoint also fails, hide image and show placeholder
                          img.style.display = 'none';
                          const placeholder = img.nextElementSibling as HTMLElement;
                          if (placeholder) {
                            placeholder.classList.remove('hidden');
                            placeholder.classList.add('bg-gray-100');
                          }
                        }
                      }}
                      onLoad={() => {
                        // Image loaded successfully, ensure placeholder is hidden
                        const img = document.querySelector(`img[alt="${vehicle.make} ${vehicle.model}"]`) as HTMLImageElement;
                        if (img && img.nextElementSibling) {
                          (img.nextElementSibling as HTMLElement).classList.add('hidden');
                        }
                      }}
                    />
                  ) : null}
                  <div className={`absolute inset-0 flex items-center justify-center bg-gray-100 ${vehicle.picture_url ? 'hidden' : ''}`}>
                    <Car className="h-16 w-16 text-gray-400" />
                  </div>

                  {/* Actions Overlay */}
                  <div className="absolute top-2 right-2 flex space-x-1">
                    <button
                      onClick={() => handleViewVehicle(vehicle)}
                      className="p-2 bg-white bg-opacity-90 rounded-lg hover:bg-opacity-100 transition-colors"
                      title="View Details"
                    >
                      <Eye className="h-4 w-4 text-gray-600" />
                    </button>
                    <button
                      onClick={() => handleEditVehicle(vehicle)}
                      className="p-2 bg-white bg-opacity-90 rounded-lg hover:bg-opacity-100 transition-colors"
                      title="Edit Vehicle"
                    >
                      <Edit className="h-4 w-4 text-blue-600" />
                    </button>
                    <button
                      onClick={() => handleDeleteVehicle(vehicle)}
                      className="p-2 bg-white bg-opacity-90 rounded-lg hover:bg-opacity-100 transition-colors"
                      title="Delete Vehicle"
                    >
                      <Trash2 className="h-4 w-4 text-red-600" />
                    </button>
                  </div>
                </div>

                {/* Vehicle Info */}
                <div className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold text-gray-900">
                      {vehicle.make} {vehicle.model}
                    </h3>
                    {vehicle.year && (
                      <span className="text-sm text-gray-500">{vehicle.year}</span>
                    )}
                  </div>

                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">License Plate:</span>
                      <span className="font-medium">{vehicle.license_plate}</span>
                    </div>
                    {vehicle.color && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Color:</span>
                        <span className="font-medium">{vehicle.color}</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-gray-600">Added:</span>
                      <span className="font-medium">
                        {new Date(vehicle.created_at).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Add/Edit Vehicle Modal */}
        {(showAddModal || showEditModal) && (
          <VehicleFormModal
            vehicle={selectedVehicle}
            isOpen={showAddModal || showEditModal}
            onClose={() => {
              setShowAddModal(false);
              setShowEditModal(false);
              setSelectedVehicle(null);
            }}
            onSuccess={handleVehicleUpdated}
          />
        )}

        {/* Vehicle Detail Modal */}
        {showDetailModal && selectedVehicle && (
          <VehicleDetailModal
            vehicle={selectedVehicle}
            isOpen={showDetailModal}
            onClose={() => {
              setShowDetailModal(false);
              setSelectedVehicle(null);
            }}
            onEdit={() => {
              setShowDetailModal(false);
              handleEditVehicle(selectedVehicle);
            }}
            onDelete={() => {
              setShowDetailModal(false);
              handleDeleteVehicle(selectedVehicle);
            }}
          />
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && vehicleToDelete && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <div className="flex items-center space-x-3 mb-4">
                <div className="flex items-center justify-center w-10 h-10 bg-red-100 rounded-full">
                  <Trash2 className="h-5 w-5 text-red-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Delete Vehicle</h3>
                  <p className="text-gray-600">This action cannot be undone.</p>
                </div>
              </div>

              <p className="text-gray-700 mb-6">
                Are you sure you want to delete <strong>{vehicleToDelete.make} {vehicleToDelete.model}</strong>
                ({vehicleToDelete.license_plate})?
              </p>

              <div className="flex space-x-3">
                <button
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    setVehicleToDelete(null);
                  }}
                  disabled={deleting}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDeleteVehicle}
                  disabled={deleting}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center justify-center space-x-2"
                >
                  {deleting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span>Deleting...</span>
                    </>
                  ) : (
                    <span>Delete</span>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VehiclesPage;
